import moment from 'moment'
import 'moment-timezone'
// eslint-disable-next-line perfectionist/sort-imports
import { datetimeFormatter } from 'transfix-ui/components/FormattedDateTime'
import { ILocationSuggestion } from 'transfix-ui/components/SuggestLocation'
import { IShipment } from 'types'
import {
  AuctionShipment,
  AuctionShipmentInfo,
  AuctionShipmentPickupOrDelivery,
  Tag,
} from 'apps/fleet-planner/types'
import { useDriverLocation } from 'utils/hooks'
import { AllowedTimezones } from 'constants/AllowedTimezones'
import { MARKETPLACE_EQUIPMENT_TYPES } from 'constants/equipment'
import { LOAD_STATE } from '../constants/loads'
import { ILoad } from '../types/loads'

interface IGetMarketplaceValues {
  weight: number
  totalMiles: string
  name: string
  parentId: number
  targetRate: string
  shipmentInfo: AuctionShipmentInfo
  tags: Tag[]
}

interface IGetAuctionshipmentStops {
  firstStop: AuctionShipmentPickupOrDelivery
  lastStop: AuctionShipmentPickupOrDelivery
  stops: AuctionShipmentPickupOrDelivery[]
}

/**
 * @param {LOAD_STATE} status - i.e. 'dispatched'
 */
const isTrackable = (status: string): boolean => {
  switch (status) {
    case LOAD_STATE['Ready for Dispatch']:
    case LOAD_STATE.Dispatched:
    case LOAD_STATE['At Pickup']:
    case LOAD_STATE.Loading:
    case LOAD_STATE['In Transit']:
    case LOAD_STATE['In Yard']:
    case LOAD_STATE['At Dropoff']:
    case LOAD_STATE.Unloading:
    case LOAD_STATE.Delivered:
      return true
    default:
      return false
  }
}

export const getMarketplaceValues = (shipment: AuctionShipment): IGetMarketplaceValues => {
  const { shipmentInfo, tags, targetRate } = shipment || {}
  const { equipment, info } = shipmentInfo || {}
  const { totalMiles, weight } = info || {}
  const { name, parentId } = equipment || {}
  return { weight, totalMiles, name, parentId, tags, targetRate, shipmentInfo }
}

export const formatWeight = (weight?: number) => {
  if (!weight) return ''
  const roundedWeight = Math.round(weight / 1000) * 1000
  return `${roundedWeight / 1000}k lbs`
}

export const formatMpWeight = (shipment: AuctionShipment) => {
  const { weight } = getMarketplaceValues(shipment)
  return formatWeight(weight)
}

export const formatTotalMiles = (totalMiles: string) => {
  if (!totalMiles) return ''
  return `${parseInt(totalMiles, 10)} mi`
}

export const formatMpTotalMiles = (shipment: AuctionShipment) => {
  const { totalMiles } = getMarketplaceValues(shipment)
  return formatTotalMiles(totalMiles)
}

export const formatEquipment = (name: string, parentId: number) => {
  if (!name || !parentId) return ''
  const equipmentSize = name.substring(0, name.indexOf(' '))
  const equipmentName = MARKETPLACE_EQUIPMENT_TYPES.find(e => e.id === parentId)?.name
  return `${equipmentSize} ${equipmentName}`
}

export const formatMpEquipment = (shipment: AuctionShipment) => {
  const { name, parentId } = getMarketplaceValues(shipment)
  return formatEquipment(name, parentId)
}

export const getAuctionshipmentStops = (
  auctionshipment: AuctionShipment
): IGetAuctionshipmentStops => {
  const { shipmentInfo } = auctionshipment || {}
  const { deliveries, pickups } = shipmentInfo || {}
  const stops = [...pickups, ...deliveries]
  const firstStop =
    stops.find(stop => stop.stopType === 'pickup' && stop.stopSequence === 1) || stops[0]
  const lastStop =
    stops.find(stop => stop.stopType === 'delivery' && stop.stopSequence === stops.length) ||
    stops[stops.length - 1]
  return { firstStop, lastStop, stops }
}

/**
 * Get driver from shipment based on `transfixUserId`, with fallbacks
 */
const getDriverFromShipment = (shipment: IShipment): string => {
  const { info } = shipment || {}
  const { driverName } = info || {}
  return driverName ?? 'This driver'
}

// i.e. 'Middletown, NY'
const getLocationString = (location: ILocationSuggestion | undefined): string => {
  const { city = '', state = '' } = location || {}
  const locationString = location ? `${city}, ${state}` : ''
  return locationString
}

export const formatAppointmentDate = (
  startAt: string,
  endAt: string | undefined,
  timezone: string
) => {
  const displayTimezone = timezone as AllowedTimezones
  return !endAt
    ? datetimeFormatter({
        showTimezone: true,
        datetime: startAt,
        displayTimezone,
        format: 'timeWeekdayDateAndYear',
        timeShort: true,
        abbreviated: true,
      })
    : datetimeFormatter({
        datetime: startAt,
        displayTimezone,
        format: 'timeWeekdayDateAndYear',
        timeShort: true,
        abbreviated: true,
      })
        .concat(' FCFS until ')
        .concat(
          datetimeFormatter({
            showTimezone: true,
            datetime: endAt,
            displayTimezone,
            format: 'time',
            timeShort: true,
          })
        )
}

/**
 * Returns a group of conditions used for determining tracking status
 */
export const getTrackingInfo = ({
  driverLocation,
  fpLoad,
  mpShipment,
}: {
  mpShipment: IShipment
  fpLoad: ILoad
  driverLocation?: ReturnType<typeof useDriverLocation>
}) => {
  const { stops = [], status: loadStatus } = fpLoad || {}
  const [firstStop] = stops || []
  const { startAt } = firstStop || {}

  const { project44Metadata } = mpShipment || {}
  const { isTruckNumberValid } = project44Metadata || {}

  const { shipmentLocations = [], location, dateFormatted } = driverLocation || {}

  const now = moment()
  const startAtMoment = moment(startAt)
  const ninetyMinutesBeforeStartAt = startAtMoment.subtract(90, 'minutes')
  const isAfter90MinutesPrior = !!(startAt && now.isAfter(ninetyMinutesBeforeStartAt))
  const isBefore90MinutesPrior = !!(startAt && now.isBefore(ninetyMinutesBeforeStartAt))
  const isBeforeTrackingStarts = isBefore90MinutesPrior

  const isDelivered = loadStatus === LOAD_STATE.Delivered

  const isTrackableStatus = isTrackable(loadStatus)
  const isActivelyTracking = !!(isTruckNumberValid && isAfter90MinutesPrior)

  const driverName = getDriverFromShipment(mpShipment)
  const { trackingStatus } = mpShipment || {}
  const { trackingMethod: trackingMethodProp = '', trackingState } = trackingStatus || {}
  const trackingMethod = trackingMethodProp.toLowerCase()

  const hasNoLocations = shipmentLocations.length === 0
  const shouldDisplayTooltip = !!mpShipment?.id && isActivelyTracking && hasNoLocations

  const locationString = getLocationString(location)

  return {
    isBeforeTrackingStarts,
    isAfter90MinutesPrior,
    isActivelyTracking,
    isTrackableStatus,
    isDelivered,
    driverName,
    trackingMethod,
    trackingState,
    shouldDisplayTooltip,
    isTruckNumberValid,
    hasNoLocations,
    locationString,
    dateFormatted,
  }
}
